'use strict';
import { cn_beam } from '../model/cn_beam';
import { cn_svg_map } from './cn_svg_map';
import { cn_3d_line_handler } from './cn_3d_line_handler';
import { fh_clone } from '@acenv/fh-3d-viewer';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_type_pastille } from './cn_type_pastille';
import { cn_edit_box } from './cn_edit_box';
import { cn_number_input } from './cn_inputs';
import { cn_column } from '../model/cn_column';
import { cn_3d_object_handler } from './cn_3d_object_handler';
import { cn_add, cn_clone, cnx_add } from '../utils/cn_utilities';
import { cn_edition_handler } from './cn_edition_handler';
import { cn_handler_wall_distance, cn_pastille } from '..';
import { CN_CURRENT_DATE } from '../utils/cn_transaction_manager';
import { cn_duplicator_handler } from './cn_duplicator_handler';
import { cn_duplicator } from '../model/cn_duplicator';

export class cn_beam_handler extends cn_edition_handler {
    //***********************************************************************************
    /**
     * Constructor
     * @param {Array<cn_beam | cn_column>} beams
     * @param {cn_svg_map} map
     * @param {boolean} creation
     */
    constructor(beams, map, creation = false) {
        super(beams, map);

        this._map = map;
        this._scene = this._map._scene;
        this._transaction_manager = this._map._building.transaction_manager;

        this._distance_handler = null;
        this._beam_handler = null;
        this._column_handler = null;


        this._pending_changes = false;

        //*** beam
        this.beams = beams;
        this._beam = (beams.length > 1) ? null : beams[0];
        if (this._beam) this._initialize_edition();

        this._current_vertex_index = 0;

        //*** Edit box for mass selection */
        const edit_box = new cn_edit_box(this, beams, this._readOnly);
        this._handlers.push(edit_box);

        this._edit_box = edit_box;
        if (!this._readOnly) {
            //*** Type pastille */
            const type_pastille = new cn_type_pastille(beams, 'element_type', () => map._building.get_beam_types(), 'Type de structure', map);
            type_pastille.title = 'Modifier le type de poutre / colonne';
            if (this._beam)
                type_pastille.on("type_changed", () => this._update_distance_handler());
            edit_box.add_pastille(type_pastille);

            if (!beams.some(b => b.constructor != cn_column))
                edit_box.add_duplicator_pastille();
            edit_box.on("duplicator", () => {
                if (!edit_box.duplication_mode())
                    this._initialize_edition();
            });

            edit_box.add_lock_pastille(this._transaction_manager);
        }
        edit_box.add_select_siblings_pastille('element_type');
        edit_box.add_information_pastille('element_type');

        edit_box.on("change", () => {
            map.refresh_main_and_tool();
            this._pending_changes = true;
        })
    }

    //***********************************************************************************
    //**** Draws
    //***********************************************************************************
    draw(camera) {
        var html = '';

        if (this._edit_box.duplication_mode())
            return this._edit_box.draw(camera);

        if (this._beam) html += this._beam.draw(camera, ['selected']);

        if (this._beam_handler) this._beam_handler.visible = this._beam_handler.active = !this._beam.locked;
        if (this._column_handler) this._column_handler.visible = this._column_handler.active = !this._beam.locked;

        html += super.draw(camera);

        return html;
    }

    //***********************************************************************************
    //**** clear move effects
    //***********************************************************************************
    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    move(ev) {

        if (this._pending_changes) {
            this._pending_changes = false;
            this._scene.update_deep();
        }

        if (this._edit_box.duplication_mode())
            return this._edit_box.move(ev);

        if (super.move(ev)) return true;

        return this._beam && this._beam.contains(ev.mouse_world, ev.camera.snap_world_distance);
    }

    _initialize_edition() {
        if (!this._beam) return;
        if (this._map.get_readonly()) {
            return;
        }
        if (this._beam.constructor == cn_beam)
            this._edit_beam(this._beam);
        else if (this._beam.constructor == cn_column)
            this._edit_column(this._beam);
    }

    /**
     *
     * @param {cn_beam} beam
     */
    _edit_beam(beam) {
        var obj = this;
        var scene = obj._scene;

        if (!this._distance_handler) {
            this._distance_handler = new cn_handler_wall_distance(this._scene, this._map);
            this._handlers.push(this._distance_handler);
            this._beam_handler = new cn_3d_line_handler(this, beam.vertices);;
            this._handlers.push(this._beam_handler);

            this._beam_handler.on('height_click', index => {
                const input = new cn_number_input('Hauteur', beam.vertices[index][2], 'm', 2, -100, 100);
                input.checkbox_label = 'Jusqu\'au plafond';
                input.checkbox_status = (beam.vertices[index][2] == 0);
                input.callback = () => {
                    obj._transaction_manager.push_transaction('Hauteur de poutre');
                    obj._transaction_manager.push_item_set(beam, 'vertices');
                    beam.vertices[index][2] = (input.checkbox_status) ? 0 : input.value;
                    this._beam_handler.vertices[index][2] = (input.checkbox_status) ? 0 : input.value;
                    obj._map.refresh_tool();
                }
                obj.call('number_input', input);
                return true;
            });

            //*** Callback on change */
            this._beam_handler.on('change', () => {
                obj._transaction_manager.push_transaction('Modification de poutre', beam.ID);
                obj._transaction_manager.push_item_set(obj._beam, 'vertices');
                beam.vertices[0] = fh_clone(this._beam_handler.vertices[0]);
                beam.vertices[1] = fh_clone(this._beam_handler.vertices[1]);
                obj._update_distance_handler();
            });

            this._distance_handler.on("change", (offset) => {
                obj._transaction_manager.push_transaction('Position de poutre', "");
                obj._transaction_manager.push_item_set(beam, 'vertices');
                beam.vertices[0] = cnx_add(beam.vertices[0], offset);
                beam.vertices[1] = cnx_add(beam.vertices[1], offset);
                this._beam_handler.vertices[0] = beam.vertices[0];
                this._beam_handler.vertices[1] = beam.vertices[1];
                obj._update_distance_handler();
                obj._map.refresh();
            });
        }
        this._distance_handler.from_contour(beam.compute_contour());

        this._beam_handler.automatic_height = 0;
        this._beam_handler.snap_elements = scene.spaces.concat(scene.beams).concat(scene.columns);
        this._beam_handler.snap_elements.splice(this._beam_handler.snap_elements.indexOf(beam), 1);

    }

    /**
     *
     * @param {cn_column} column
     * @returns
     */
    _edit_column(column) {
        var obj = this;
        var scene = obj._scene;

        if (!this._distance_handler) {
            this._distance_handler = new cn_handler_wall_distance(this._scene, this._map);
            this._handlers.push(this._distance_handler);
            this._column_handler = new cn_3d_object_handler(column.position, column.orientation, 0, this._scene);
            this._handlers.push(this._column_handler);

            this._column_handler.on('change', () => {
                obj._transaction_manager.push_transaction('Position de poteau', column.ID);
                obj._transaction_manager.push_item_set(column, ['position', 'orientation']);

                column.position = cn_clone(this._column_handler.position);
                column.orientation = this._column_handler.orientation;
                obj._update_distance_handler();
                obj._pending_changes = true;
            });

            this._column_handler.on('height_click', () => {
                const input = new cn_number_input('Hauteur', (column.height) ? column.height : 0, 'm', 2, 0.1, 100);
                input.checkbox_label = 'Jusqu\'au plafond';
                input.checkbox_status = (!column.height);
                input.callback = () => {
                    const check_status = input.checkbox_status;
                    const new_value = input.value;
                    obj._transaction_manager.push_transaction('Hauteur de poteau');
                    obj._transaction_manager.push_item_set(column, 'height');
                    column.height = (check_status) ? 0 : new_value;
                    this._column_handler.height = (check_status) ? 0 : new_value;
                    obj._pending_changes = true;
                    obj._map.refresh_tool();
                }
                obj.call('number_input', input);
                return true;
            });

            this._distance_handler.on("change", (offset) => {
                obj._transaction_manager.push_transaction('Position de poteau', "");
                obj._transaction_manager.push_item_set(obj._beam, 'vertices');
                column.position = cn_add(column.position, offset);
                this._column_handler.position = column.position;
                obj._update_distance_handler();
                obj._pending_changes = true;
                obj._map.refresh();
            });

        }
        this._distance_handler.from_contour(column.compute_contour());

        //*** Create a handler for that object.
        this._column_handler.position = column.position;
        this._column_handler.orientation = column.orientation;
        this._column_handler.height = (!column.height) ? false : column.height;
        this._column_handler.snap_elements = scene.spaces.concat(scene.beams).concat(scene.columns);
        this._column_handler.snap_elements.splice(this._column_handler.snap_elements.indexOf(column), 1);

        return true;
    }

    _update_distance_handler() {
        if (this._distance_handler && this._beam)
            this._distance_handler.from_contour(this._beam.compute_contour());

    }
}

function cn_get_date() {
    throw new Error('Function not implemented.');
}

